import type { CRMUser, EmployeeDesignation, ExecutiveBriefingProfile, User } from '../types';

/** Single source of truth — keep in sync with `EmployeeDesignation` in types and AuthContext parsing. */
export const EMPLOYEE_DESIGNATIONS: readonly EmployeeDesignation[] = [
  'project_manager',
  'estimator',
  'executive_assistant',
  'sales_rep',
  'cfo',
  'coo',
  'office_manager',
];

export function parseEmployeeDesignations(raw: unknown): EmployeeDesignation[] | undefined {
  if (!Array.isArray(raw) || raw.length === 0) return undefined;
  const allowed = EMPLOYEE_DESIGNATIONS as readonly string[];
  const out = raw.filter(
    (x): x is EmployeeDesignation => typeof x === 'string' && allowed.includes(x),
  );
  return out.length ? out : undefined;
}

export function hasEmployeeDesignation(
  designations: EmployeeDesignation[] | string[] | undefined,
  designation: EmployeeDesignation,
): boolean {
  return !!designations?.includes(designation);
}

/** Elevates scoped roles (e.g. Employee — Office) to full CRM employee access. */
export function hasOfficeManagerDesignation(
  designations: EmployeeDesignation[] | string[] | undefined,
): boolean {
  return hasEmployeeDesignation(designations, 'office_manager');
}

/**
 * Office-ops extras historically tied to Executive Assistant (finances write,
 * time-off approval, Agent Activity, card expenses on behalf, etc.).
 * Office Manager includes these privileges.
 */
export function hasOfficeOpsPrivileges(
  designations: EmployeeDesignation[] | string[] | undefined,
): boolean {
  return (
    hasEmployeeDesignation(designations, 'executive_assistant')
    || hasEmployeeDesignation(designations, 'office_manager')
  );
}

/** Admin / Employee — Full, or anyone with the Office Manager designation. */
export function hasFullCrmEmployeeAccess(args: {
  authRole?: string | null;
  designations?: EmployeeDesignation[] | string[];
}): boolean {
  const role = String(args.authRole || '').trim().toLowerCase();
  if (role === 'admin' || role === 'employee') return true;
  return hasOfficeManagerDesignation(args.designations);
}

/** W-2 roles that may take the Sales Rep CRM slice. */
export function isW2SalesEligibleRole(role?: string | null): boolean {
  const r = String(role || '').trim().toLowerCase();
  return (
    r === 'admin'
    || r === 'employee'
    || r === 'employee_office'
    || r === 'employee_shop'
    || r === 'staff'
  );
}

/**
 * Sales CRM slice: full Employee (or Office Manager) **or** `sales_rep` on a W-2 role.
 * Does not grant Finances, UTM, Hiring, or workspace Settings.
 */
export function hasSalesCrmAccess(args: {
  authRole?: string | null;
  designations?: EmployeeDesignation[] | string[];
}): boolean {
  if (hasFullCrmEmployeeAccess(args)) return true;
  if (!isW2SalesEligibleRole(args.authRole)) return false;
  return hasEmployeeDesignation(args.designations, 'sales_rep');
}

export function userHasSalesRepDesignation(
  user: Pick<User, 'designations'> | Pick<CRMUser, 'designations'> | null | undefined,
): boolean {
  return hasEmployeeDesignation(user?.designations, 'sales_rep');
}

export function getProjectManagers(crmUsers: CRMUser[]): CRMUser[] {
  return crmUsers.filter(
    u =>
      u.isActive &&
      (u.role === 'employee' || u.role === 'admin') &&
      hasEmployeeDesignation(u.designations, 'project_manager'),
  );
}

/** True when the signed-in user is an active employee/admin with the project_manager designation. */
export function userIsProjectManager(
  user: Pick<User, 'id' | 'role'> | null | undefined,
  crmUsers: CRMUser[],
): boolean {
  if (!user?.id) return false;
  const row = crmUsers.find(u => u.uid === user.id);
  return !!(
    row &&
    row.isActive &&
    (row.role === 'employee' || row.role === 'admin') &&
    hasEmployeeDesignation(row.designations, 'project_manager')
  );
}

export function isCooDesignation(args: {
  designations?: EmployeeDesignation[] | string[];
  briefingProfile?: ExecutiveBriefingProfile | null;
}): boolean {
  return !!args.designations?.includes('coo') || args.briefingProfile === 'coo';
}

/** PMs, COOs, and admins may release / close / mark filled crew jobs. */
export function canReleaseCrewJobs(args: {
  authRole?: string;
  designations?: EmployeeDesignation[] | string[];
  briefingProfile?: ExecutiveBriefingProfile | null;
}): boolean {
  const role = String(args.authRole || '').toLowerCase();
  if (role === 'admin') return true;
  if (role !== 'employee') return false;
  if (hasEmployeeDesignation(args.designations, 'project_manager')) return true;
  return isCooDesignation(args);
}

export function userCanReleaseCrewJobs(
  user: Pick<User, 'id' | 'role'> | null | undefined,
  crmUsers: CRMUser[],
): boolean {
  if (!user?.id) return false;
  const row = crmUsers.find(u => u.uid === user.id);
  if (!row || row.isActive === false) return false;
  return canReleaseCrewJobs({
    authRole: row.role || user.role,
    designations: row.designations,
    briefingProfile: row.executiveBriefing?.enabled ? row.executiveBriefing.profile : row.executiveBriefing?.profile,
  });
}

export function getSalesReps(crmUsers: CRMUser[]): CRMUser[] {
  return crmUsers.filter(
    u =>
      u.isActive &&
      isW2SalesEligibleRole(u.role) &&
      hasEmployeeDesignation(u.designations, 'sales_rep'),
  );
}
